import React, { useState } from "react";
import { SimpleModulePage } from "../modules/SimpleModulePage";

const SectionCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
  <div className={`mx-4 mt-3 rounded-2xl bg-white shadow-sm px-4 py-3 mb-3 border border-slate-100 ${className}`}>{children}</div>
);

interface EnvironmentSettingsPageProps {
  onBack: () => void;
  onMenuClick?: (menu: string) => void;
}

// 약관 내용 정의
const termsContent = {
  "service-terms": {
    title: "서비스 약관",
    content: `제1조 목적
본 약관은 허비서AI(이하 "회사")가 제공하는 서비스(이하 "서비스")의 이용 조건 및 절차, 이용자와 회사의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.

제2조 용어의 정의
1. "서비스"란 회사가 제공하는 건강 관리 및 의약품 복용 관리 서비스를 의미합니다.
2. "이용자"란 회사의 서비스에 접속하여 이 약관에 동의하고 서비스를 이용하는 개인을 의미합니다.
3. "개인정보"란 이용자의 성명, 주소, 연락처 등 개인을 식별할 수 있는 정보를 의미합니다.

제3조 서비스 이용 계약
1. 회사는 이용자가 본 약관에 동의한 경우 서비스 가입을 승인합니다.
2. 이용자가 만 18세 미만인 경우, 법정대리인의 동의가 필요합니다.

제4조 이용자의 의무
1. 이용자는 본 약관에 규정된 사항 및 관계 법령을 준수하여야 합니다.
2. 이용자는 서비스를 이용하면서 타인의 개인정보를 무단으로 수집하거나 공개해서는 안 됩니다.`,
  },
  "privacy-policy": {
    title: "개인정보 처리방침",
    content: `제1조 개인정보의 수집 및 이용
회사는 다음과 같은 개인정보를 수집합니다:
- 필수: 이름, 이메일, 휴대폰 번호, 건강 정보
- 선택: 프로필 사진, 생년월일
수집된 개인정보는 서비스 제공, 고객 상담, 마케팅 활동 등의 목적으로 이용됩니다.

제2조 개인정보의 보유 및 이용 기간
원칙적으로 개인정보는 수집 및 이용 목적이 달성되면 즉시 파기합니다.
다만, 관련 법령의 규정에 따라 일정 기간 보관할 수 있습니다.

제3조 개인정보의 안전성 확보
회사는 이용자의 개인정보를 보호하기 위해 다음과 같은 조치를 취합니다:
- 암호화된 통신
- 접근 권한 제한
- 주기적인 보안 감시

제4조 이용자의 권리
이용자는 언제든지 본인의 개인정보에 대한 열람, 정정, 삭제를 요청할 수 있습니다.`,
  },
  "third-party-provision": {
    title: "개인정보 제3자 제공",
    content: `제1조 제3자 제공 정책
회사는 이용자의 동의 없이 개인정보를 제3자에게 제공하지 않습니다.

제2조 예외적 제공
다음의 경우에는 이용자의 동의 없이 개인정보를 제3자에게 제공할 수 있습니다:
1. 법령의 규정에 기반한 경우
2. 수사기관의 정당한 요청이 있는 경우
3. 서비스 제공을 위해 필요한 경우

제3조 제3자 목록
다음 기관들과 개인정보를 공유할 수 있습니다:
- 의료기관: 건강 상담을 위해 사용자 동의 시
- 약국: 약품 정보 조회를 위해 사용자 동의 시
- 결제 서비스 제공자: 결제 처리를 위해

제4조 제3자의 개인정보 보호
제3자는 회사와 동일한 수준의 개인정보 보호 의무를 갖습니다.`,
  },
  "location-service-terms": {
    title: "위치정보서비스 약관",
    content: `제1조 목적
본 약관은 허비서AI가 제공하는 위치정보 기반 서비스의 이용 조건을 규정합니다.

제2조 위치정보 수집
1. 서비스 이용을 위해 다음과 같은 위치정보를 수집합니다:
   - 사용자의 현재 위도/경도
   - 근처 약국 정보 제공
   - 운동 기록 (걸음수, 이동 거리)

제3조 이용자의 동의
1. 이용자는 위치정보 수집에 대해 별도로 동의할 수 있습니다.
2. 이용자는 언제든지 위치정보 수집을 거부할 수 있습니다.

제4조 위치정보 보호
1. 이용자의 위치정보는 안전하게 암호화되어 저장됩니다.
2. 회사는 위치정보를 제3자에게 제공하지 않습니다.

제5조 기술적 조치
회사는 위치정보 오유용 방지를 위해 다음 조치를 취합니다:
- 주기적인 보안 검토
- 접근 권한 제한
- 이용 기록 관리`,
  },
};

export const EnvironmentSettingsPage: React.FC<EnvironmentSettingsPageProps> = ({ onBack, onMenuClick }) => {
  // 선택 약관 상태
  const [pushNotificationAgree, setPushNotificationAgree] = useState(true);
  const [marketingInfoAgree, setMarketingInfoAgree] = useState(false);
  
  // 텍스트 확장 상태
  const [pushNotificationExpanded, setPushNotificationExpanded] = useState(false);
  const [marketingInfoExpanded, setMarketingInfoExpanded] = useState(false);
  
  // 모달 상태
  const [selectedTerm, setSelectedTerm] = useState<string | null>(null);

  const handleTermsClick = (term: string) => {
    setSelectedTerm(term);
    if (onMenuClick) {
      onMenuClick(term);
    }
  };

  const getTermsContent = () => {
    if (!selectedTerm || !termsContent[selectedTerm as keyof typeof termsContent]) {
      return null;
    }
    return termsContent[selectedTerm as keyof typeof termsContent];
  };

  const currentTermsContent = getTermsContent();

  return (
    <SimpleModulePage 
      title="환경설정" 
      subtitle="(선택 및 필수 약관)" 
      icon="⚙️" 
      onBack={onBack}
    >
      {/* 선택 약관 섹션 */}
      <div>
        <h2 className="px-4 text-[13px] font-semibold text-slate-900 mb-3">
          선택 약관
        </h2>
        <SectionCard>
          <div className="space-y-3">
            {/* 푸시 동의 */}
            <div className="flex items-start justify-between py-2">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 mb-1">
                  푸시 동의
                </div>
                <p 
                  onClick={() => setPushNotificationExpanded(!pushNotificationExpanded)}
                  style={{ fontSize: 'clamp(0.65rem, 2.5vw, 0.875rem)' }}
                  className={`text-slate-600 cursor-pointer transition-all ${
                    pushNotificationExpanded 
                      ? "leading-relaxed" 
                      : "leading-none truncate"
                  }`}
                >
                  공시사항, 당첨자 발표, 결제 관련 알림 등의 정보성 알림을 받을 수 있어요!
                </p>
              </div>
              <button
                onClick={() => setPushNotificationAgree(!pushNotificationAgree)}
                className={`ml-3 w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                  pushNotificationAgree ? "bg-emerald-500" : "bg-slate-300"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full bg-white transition-transform ${
                    pushNotificationAgree ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>

            {/* 구분선 */}
            <div className="border-t border-slate-100" />

            {/* 마케팅 정보 제공 */}
            <div className="flex items-start justify-between py-2">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-slate-900 mb-1">
                  마케팅 정보 제공
                </div>
                <p 
                  onClick={() => setMarketingInfoExpanded(!marketingInfoExpanded)}
                  style={{ fontSize: 'clamp(0.65rem, 2.5vw, 0.875rem)' }}
                  className={`text-slate-600 cursor-pointer transition-all ${
                    marketingInfoExpanded 
                      ? "leading-relaxed" 
                      : "leading-none truncate"
                  }`}
                >
                  허비서AI 소식과 이벤트 정보를 원한다면 동의를 눌러주세요!
                </p>
              </div>
              <button
                onClick={() => setMarketingInfoAgree(!marketingInfoAgree)}
                className={`ml-3 w-12 h-7 rounded-full transition-colors flex-shrink-0 ${
                  marketingInfoAgree ? "bg-emerald-500" : "bg-slate-300"
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full bg-white transition-transform ${
                    marketingInfoAgree ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* 필수 약관 섹션 */}
      <div>
        <h2 className="px-4 text-[13px] font-semibold text-slate-900 mb-3">
          필수 약관
        </h2>
        <SectionCard>
          <div className="space-y-0">
            <button
              onClick={() => handleTermsClick('service-terms')}
              className="w-full py-3 px-0 text-left text-[14px] text-slate-700 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors flex items-center justify-between"
            >
              <span>서비스 약관</span>
              <span className="text-slate-400">→</span>
            </button>
            <button
              onClick={() => handleTermsClick('privacy-policy')}
              className="w-full py-3 px-0 text-left text-[14px] text-slate-700 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors flex items-center justify-between"
            >
              <span>개인정보 처리방침</span>
              <span className="text-slate-400">→</span>
            </button>
            <button
              onClick={() => handleTermsClick('third-party-provision')}
              className="w-full py-3 px-0 text-left text-[14px] text-slate-700 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors flex items-center justify-between"
            >
              <span>개인정보 제3자 제공</span>
              <span className="text-slate-400">→</span>
            </button>
            <button
              onClick={() => handleTermsClick('location-service-terms')}
              className="w-full py-3 px-0 text-left text-[14px] text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-between"
            >
              <span>위치정보서비스 약관</span>
              <span className="text-slate-400">→</span>
            </button>
          </div>
        </SectionCard>
      </div>

      {/* 안내 */}
      <SectionCard className="bg-blue-50 border-blue-200">
        <div className="flex gap-2">
          <span className="text-[16px] flex-shrink-0">ℹ️</span>
          <div>
            <p className="text-[12px] font-semibold text-blue-900 mb-1">설정 안내</p>
            <p className="text-[11px] text-blue-800 leading-relaxed">
              선택 약관은 언제든지 변경할 수 있습니다. 필수 약관은 서비스 이용을 위해 반드시 동의해야 합니다.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* 약관 내용 모달 */}
      {selectedTerm && currentTermsContent && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={() => setSelectedTerm(null)}>
          <div className="w-full max-w-md rounded-t-2xl bg-white px-5 pt-2 pb-5 shadow-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* 드래그 핸들 */}
            <div className="w-10 h-1 rounded-full bg-slate-300 mx-auto mb-4" />
            
            {/* 헤더 */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[15px] font-semibold text-slate-900">
                {currentTermsContent.title}
              </h3>
              <button
                onClick={() => setSelectedTerm(null)}
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center hover:bg-slate-200 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* 내용 스크롤 영역 */}
            <div className="flex-1 overflow-y-auto mb-4 pr-2">
              <div className="text-[12px] text-slate-700 leading-relaxed whitespace-pre-line">
                {currentTermsContent.content}
              </div>
            </div>

            {/* 하단 버튼 */}
            <button
              onClick={() => setSelectedTerm(null)}
              className="w-full h-10 rounded-lg bg-violet-600 text-white text-[13px] font-semibold hover:bg-violet-700 transition-colors"
            >
              확인했습니다
            </button>
          </div>
        </div>
      )}
    </SimpleModulePage>
  );
};
